import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBookingStore } from "@/stores/bookingStore";
import { useMembership } from "@/contexts/MembershipContext";
import { t as translate } from "@/locales";
import type { BookingLanguage } from "@/types/booking";
import { useVoucherValidation, type VoucherItem } from "./useVoucherValidation";

interface InvitationCodeInputProps {
  language: BookingLanguage;
  items?: VoucherItem[];
}

export function InvitationCodeInput({
  language,
  items = [],
}: InvitationCodeInputProps) {
  const { isMember } = useMembership();

  const t = {
    label: translate(language, "invitationCode.label"),
    optional: translate(language, "invitationCode.optional"),
    apply: translate(language, "invitationCode.apply"),
    remove: translate(language, "invitationCode.remove"),
  };

  // Use booking store for state
  const invitationCode = useBookingStore((state) => state.invitationCode);
  const discount = useBookingStore((state) => state.discount);
  const setInvitationCode = useBookingStore((state) => state.setInvitationCode);
  const clearInvitationCode = useBookingStore(
    (state) => state.clearInvitationCode,
  );

  const isApplied = !!discount;

  const { validateVoucher, loading } = useVoucherValidation(language, items);

  // Re-validate when items change if a code is already applied
  const itemsHash = JSON.stringify(
    items.map((i) => i.stripe_product_id).sort(),
  );

  useEffect(() => {
    if (isApplied && invitationCode) {
      validateVoucher(true);
    }
  }, [itemsHash]);

  const handleRemove = () => {
    clearInvitationCode();
  };

  // Members already get membership pricing — no stacking an invitation discount.
  if (isMember) return null;

  return (
    <div className="space-y-2">
      <Label htmlFor="invitation-code">
        {t.label}{" "}
        <span className="font-normal text-neutral-500">{t.optional}</span>
      </Label>
      <div className="flex gap-2">
        <Input
          id="invitation-code"
          value={invitationCode}
          onChange={(e) => setInvitationCode(e.target.value)}
          disabled={loading || isApplied}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              validateVoucher(false);
            }
          }}
        />
        {isApplied ? (
          <Button
            type="button"
            variant="outline"
            onClick={handleRemove}
            disabled={loading}
            className="normal-case"
          >
            {t.remove}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => validateVoucher(false)}
            disabled={loading || !invitationCode.trim()}
            className="normal-case"
          >
            {loading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              t.apply
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
